<script>
  /**
   * Upcoming company closures, for the staff home and the customer dashboard.
   *
   * The list is company-wide: `non_working_days` has no customer column, so
   * both sides read exactly the same rows and there is nothing to scope.
   *
   * The horizon is a window, not the whole table. Ninety days is a quarter's
   * notice, which is what the two shutdowns anyone actually plans around need
   * — Ferragosto in August and the Christmas/New Year week — while keeping the
   * panel a short list instead of a calendar.
   */
  import { onMount } from 'svelte';
  import { _ } from '../lib/i18n.js';
  import { nonWorkingDaysApi } from '../stores/api.js';
  import { CalendarOff, AlertCircle, Loader } from 'lucide-svelte';

  /** @type {number} Days ahead to look. */
  export let horizonDays = 90;
  /** @type {number} Rows to show before collapsing the rest into a count. */
  export let maxRows = 5;

  let days = [];
  let loading = true;
  let error = null;

  onMount(async () => {
    await load();
  });

  /**
   * Today and the end of the horizon as ISO days, built from local date parts.
   *
   * `toISOString()` would read the local day back in UTC and can shift the
   * boundary by one day west of Greenwich — the same trap the backend's
   * serializeDate guards against from the other direction.
   *
   * @returns {{from: string, to: string}}
   */
  function window_() {
    const isoDay = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };
    const today = new Date();
    const end = new Date(today.getFullYear(), today.getMonth(), today.getDate() + horizonDays);
    return { from: isoDay(today), to: isoDay(end) };
  }

  async function load() {
    loading = true;
    error = null;
    try {
      const { from, to } = window_();
      days = await nonWorkingDaysApi.getBetween(from, to) || [];
      console.log(`[ClosureDaysPanel] Loaded ${days.length} closures between ${from} and ${to}`);
    } catch (err) {
      console.error('[ClosureDaysPanel] Load error:', err);
      error = err.message;
    } finally {
      loading = false;
    }
  }

  $: visibleDays = days.slice(0, maxRows);
  $: hiddenCount = Math.max(days.length - visibleDays.length, 0);

  /**
   * An ISO day, written the way a person reads a date.
   *
   * Parsed with an explicit local midnight: `new Date('2026-12-25')` is UTC
   * midnight and renders as the 24th anywhere west of Greenwich.
   *
   * @param {string} iso
   * @returns {string}
   */
  function formatDay(iso) {
    if (!iso) return '-';
    const date = new Date(`${String(iso).slice(0, 10)}T00:00:00`);
    if (Number.isNaN(date.getTime())) return String(iso);
    return new Intl.DateTimeFormat('it-IT', { weekday: 'short', day: 'numeric', month: 'long' }).format(date);
  }
</script>

<div class="card-elevated p-6">
  <div class="flex items-center space-x-3 mb-4">
    <div class="w-10 h-10 border-2 border-primary-500 flex items-center justify-center" style="background: rgba(0, 240, 255, 0.1);">
      <CalendarOff size={20} class="text-primary-700 dark:text-primary-400" />
    </div>
    <div>
      <h3 class="text-lg font-bold text-heading font-display tracking-wider">{$_('closureDays.title')}</h3>
      <p class="text-sm text-body">{$_('closureDays.subtitle', { values: { days: horizonDays } })}</p>
    </div>
  </div>

  {#if loading}
    <div class="flex items-center gap-2 py-4 text-body">
      <Loader size={16} class="animate-spin text-primary-500" />
      <span class="text-sm">{$_('common.loading')}</span>
    </div>
  {:else if error}
    <div class="flex items-start gap-2 py-4 text-sm text-body">
      <AlertCircle size={16} class="text-red-500 flex-shrink-0 mt-0.5" />
      <span>{$_('closureDays.loadError')}</span>
    </div>
  {:else if days.length === 0}
    <!--
      An empty list is the ordinary answer today, not a failure: the panel says
      so in one calm line rather than rendering an empty frame that reads as
      something that failed to load.
    -->
    <p class="text-sm text-body py-2">{$_('closureDays.empty', { values: { days: horizonDays } })}</p>
  {:else}
    <ul class="space-y-2">
      {#each visibleDays as day (day.date)}
        <li class="flex items-center justify-between gap-3 p-3 border-l-4 bg-surface-alt {day.kind === 'closure' ? 'border-magenta-500' : 'border-primary-500'}">
          <div class="min-w-0">
            <div class="text-sm font-bold text-heading">{formatDay(day.date)}</div>
            <div class="text-sm text-body truncate">{day.label}</div>
          </div>
          <span class="text-xs font-bold uppercase tracking-wider text-body flex-shrink-0">
            {$_(`settings.nonWorkingDays.kinds.${day.kind}`)}
          </span>
        </li>
      {/each}
    </ul>
    {#if hiddenCount > 0}
      <p class="mt-3 text-xs text-body">{$_('closureDays.more', { values: { count: hiddenCount } })}</p>
    {/if}
  {/if}
</div>
